import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { CobuView, CobuSpacer } from '@cobu/ui'
import { RootState } from '../../store'
import { setUser } from '../../store/authSlice'
import { useQueryClient } from '@tanstack/react-query'
import { userService } from '@cobu/api-client'

import ProfileSection from '../../components/profile/ProfileSection'
import ProfileMenuItem from '../../components/profile/ProfileMenuItem'
import ProfileSwitchItem from '../../components/profile/ProfileSwitchItem'
import ProfileVersionInfo from '../../components/profile/ProfileVersionInfo'
import ProfileHeader from '../../components/profile/ProfileHeader'
import { Strings } from '../../constants/Strings';
import { analyticsService } from '@cobu/analytics';
import { openIntercom } from '../../intercom';
import { logger } from '../../utils/logger';

export default function Profile() {
  const { id } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state: RootState) => state.auth.user)
  const appVersion = import.meta.env.VITE_APP_VERSION
  const [showFullName, setShowFullName] = useState<boolean>(!!user?.showFullName)
  const qc = useQueryClient()
  const strings = Strings.get();
  useEffect(() => {
    setShowFullName(!!user?.showFullName)
  }, [user?.showFullName])

  useEffect(() => {
    if (id) {
      analyticsService.trackEvent('VIEW_PUBLIC_PROFILE', { userId: id });
    }
  }, [id]);

  // Handlers (stubbed or simplified similar to groups.tsx approach)
  const handleToggleFullName = useCallback(async (value: boolean) => {
    if (!user?.uuid) return
    setShowFullName(value)
    try {
      await userService.updateUser(user.uuid, { showFullName: value })
      dispatch(setUser({ ...user, showFullName: value }))
      await qc.invalidateQueries({ queryKey: ['user_getMyProfile'] })
      await qc.invalidateQueries({ queryKey: ['public-profile', user.uuid] })
    } catch (_e) {
      // revert optimistic toggle if needed
      setShowFullName(!!user.showFullName)
    }
  }, [user, qc, dispatch])

  const handleOpenSupport = useCallback(() => {
    window.open('https://intercom.help/cobu/en/', '_blank', 'noopener')
  }, [])

  const handleContactSupport = useCallback(() => {
    window.open('mailto:info@livecobu.com?subject=App%20support')
  }, [])

  const handleBlockedUsers = useCallback(() => {
    navigate('/profile/blocked-users')
  }, [navigate])

  const handleReviewBuilding = useCallback(() => {
    if (user?.building?.reviewUrl) {
      logger.log('press_review_building_link', {
        email: user?.email,
        firstName: user?.firstName,
        lastName: user?.lastName,
        buildingName: user?.building?.name,
      })
      window.open(user.building.reviewUrl, '_blank', 'noopener')
    }
  }, [user])

  const handleChatWithCobu = useCallback(() => {
    void openIntercom()
  }, [])

  // Early empty state similar to groups.tsx minimalism
  if (!user) return null

  const helpItems = [
    { label: strings.profile.menuItems.chatWithCobu, onPress: handleChatWithCobu },
    { label: strings.profile.menuItems.support, onPress: handleOpenSupport },
    { label: strings.profile.menuItems.blockedUsers, onPress: handleBlockedUsers },
    { label: strings.profile.menuItems.contactUs, onPress: handleContactSupport },
    ...(user?.building?.reviewUrl ? [{ label: strings.profile.menuItems.reviewBuilding, onPress: handleReviewBuilding }] : []),
    ...(user?.building?.residentPortalURL
      ? [{ label: strings.profile.menuItems.residentPortal, onPress: () => window.open(user.building!.residentPortalURL!, '_blank', 'noopener') }]
      : []),
    ...(user?.building?.contactInfoURL
      ? [{ label: strings.profile.menuItems.buildingContactInfo, onPress: () => window.open(user.building!.contactInfoURL!, '_blank', 'noopener') }]
      : []),
  ]

  return (
    <CobuView padding="$0" paddingBottom="$10" gap="$0" backgroundColor="$backgroundSecondary">
      {/* Header */}
      <ProfileHeader user={user ? { ...user, showFullName } : user} onPress={() => navigate(`/public-profile/${user.uuid}`)} />

      {/* Account Section */}
      <ProfileSection title={strings.profile.sections.account}>
        <ProfileSwitchItem
          title={strings.profile.switches.showFullName}
          value={showFullName}
          onValueChange={handleToggleFullName}
        />
      </ProfileSection>

      {/* Help & Settings */}
      <ProfileSection title={strings.profile.sections.helpAndSettings}>
        {helpItems.map((item, idx) => (
          <ProfileMenuItem
            key={item.label}
            title={item.label}
            onPress={item.onPress}
            isFirstItem={idx === 0}
            isLastItem={idx === helpItems.length - 1}
          />
        ))}
      </ProfileSection>

      <ProfileVersionInfo appVersion={appVersion} />

      <CobuSpacer size="$6" />
    </CobuView>
  )
}
